import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button, Card, TextField, Typography, CircularProgress, Alert, Container, Box } from "@mui/material";
import { UserApiExtended } from "../../api/UserApi";
import UnauthNav from "../../components/UnauthNav/UnauthNav";

import "./UserPasswordResetView.scss";

const UserPasswordResetView = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Invalid password reset link. Please request a new password reset.");
    }
  }, [token]);

  const validatePassword = (password: string): string => {
    if (password.length < 8 || password.length > 20) {
      return "Password must be between 8 and 20 characters";
    }
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@.#$!%^&*.?])[A-Za-z\d@.#$!%^&*.?]/.test(password)) {
      return "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character";
    }
    return "";
  };

  const handlePasswordChange = (password: string) => {
    setNewPassword(password);
    const validation = validatePassword(password);
    setPasswordError(validation);
  };

  const handleSubmit = async () => {
    if (!token) {
      setError("Invalid password reset link");
      return;
    }

    if (!newPassword || !confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    const validation = validatePassword(newPassword);
    if (validation) {
      setError(validation);
      return;
    }

    setLoading(true);
    setError("");

    try {
      await UserApiExtended.confirmPasswordReset(token, newPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err: any) {
      setError(err.message || "Failed to reset password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !loading && newPassword && confirmPassword) {
      handleSubmit();
    }
  };

  if (success) {
    return (
      <>
        <UnauthNav />
        <Container maxWidth="sm" className="password-reset-container">
          <Card className="password-reset-card">
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="h6">Password Reset Successful!</Typography>
              <Typography variant="body2">
                Your password has been reset successfully. You will be redirected to the login page in a few seconds.
              </Typography>
            </Alert>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => navigate("/login")}
              fullWidth
            >
              Go to Login
            </Button>
          </Card>
        </Container>
      </>
    );
  }

  return (
    <>
      <UnauthNav />
      <Container maxWidth="sm" className="password-reset-container">
        <Card className="password-reset-card">
          <Typography variant="h4" component="h1" className="password-reset-title">
            Reset Your Password
          </Typography>
          
          <Typography variant="body2" color="textSecondary" className="password-reset-subtitle">
            Please enter your new password below. Make sure it's secure and easy for you to remember.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box className="password-reset-form">
            <TextField
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => handlePasswordChange(e.target.value)}
              onKeyDown={handleKeyPress}
              variant="outlined"
              fullWidth
              disabled={loading}
              error={!!passwordError}
              helperText={passwordError || "8-20 characters with uppercase, lowercase, number, and special character"}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onKeyDown={handleKeyPress}
              variant="outlined"
              fullWidth
              disabled={loading}
              error={newPassword !== confirmPassword && confirmPassword.length > 0}
              helperText={newPassword !== confirmPassword && confirmPassword.length > 0 ? "Passwords do not match" : ""}
              sx={{ mb: 3 }}
            />

            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading || !newPassword || !confirmPassword || !!passwordError || newPassword !== confirmPassword}
              fullWidth
              size="large"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? "Resetting Password..." : "Reset Password"}
            </Button>
          </Box>
        </Card>
      </Container>
    </>
  );
};

export default UserPasswordResetView;